/**
 * Inventory Overlay Component - Slide-in panel for graphical mode
 * Displays player inventory items in a grid layout
 */

import React, { useState, useEffect } from 'react';
import { useGameStore } from '@/state/gameStore';
import type { InventoryItem } from '@/types';

interface InventoryOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export const InventoryOverlay: React.FC<InventoryOverlayProps> = ({ isOpen, onClose }) => {
  const currentPlayerState = useGameStore((state) => state.currentPlayerState);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);

  useEffect(() => {
    // Load inventory from player state or game store
    // For now, use placeholder data
    const placeholderInventory: InventoryItem[] = [
      { id: '1', name: 'Neural Interface', quantity: 1, type: 'armor', value: 100 },
      { id: '2', name: 'Energy Cell', quantity: 5, type: 'consumable', value: 10 },
      { id: '3', name: 'Data Chip', quantity: 3, type: 'material', value: 25 },
    ];
    setInventory(placeholderInventory);
  }, [currentPlayerState]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Inventory Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-full max-w-md bg-slate-900 border-l border-cyan-500/40 shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <h2 className="text-xl font-semibold text-cyan-300">Inventory</h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors"
              aria-label="Close inventory"
            >
              ✕
            </button>
          </div>

          {/* Inventory Grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {inventory.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-400">
                <p>Your inventory is empty</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {inventory.map((item) => (
                  <div
                    key={item.id}
                    className="bg-slate-800 border border-slate-600 rounded-lg p-3 hover:border-cyan-500 transition-colors cursor-pointer group"
                  >
                    <div className="aspect-square bg-slate-700 rounded mb-2 flex items-center justify-center">
                      <span className="text-2xl">{getItemIcon(item.type)}</span>
                    </div>
                    <p className="text-sm font-medium text-cyan-300 truncate">{item.name}</p>
                    {item.quantity > 1 && (
                      <p className="text-xs text-slate-400 mt-1">x{item.quantity}</p>
                    )}
                    {item.value && (
                      <p className="text-xs text-slate-500 mt-1">${item.value}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center justify-between text-sm text-slate-400">
              <span>Total Items: {inventory.length}</span>
              <span>Press ESC to close</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Get icon for item type
 */
function getItemIcon(type?: string): string {
  switch (type) {
    case 'weapon':
      return '⚔️';
    case 'armor':
      return '🛡️';
    case 'consumable':
      return '💊';
    case 'material':
      return '⚙️';
    case 'crafted':
      return '🔧';
    default:
      return '📦';
  }
}

